import { FC, useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Box, Container, Typography, Avatar, Modal } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';

import LogoIcon from '../../assets/landing/new_logo.png';

import { useDispatch } from 'react-redux';
import { logout } from '../../store/slices/authSlice';
import { useGetUserQuery } from '../../store/services/customizeOptionsApi';
import { useAppSelector } from '../../hooks/redux';
import { logOutUser, setUserInfo } from '../../store/slices/userSlice';
import { api } from '../../store/services/api';
import { createGradientConfig } from '../../utils/gradientUtils';

import styles from './UserInfoWrapper.module.scss';
import {
  ExitIcon,
  LikeIcon,
  TripIcon,
  UserIcon,
  EditIcon,
  DiscoverIcon,
  CheckCircleIcon,
} from '../../components/Icons';
import AvatarUploadModal from '../AvatarUploadModal/AvatarUploadModal';
import NotificationsPopover from '../NotificationsPopover/NotificationsPopover';
import RealtimeNotifications from '../RealtimeNotifications/RealtimeNotifications';
import { unregisterNotificationDevice } from '../../services/notificationDevice';
import { clearReferralToken } from '../../constants/referralToken';
import { setPlans, setSelectedPlan } from '../../store/slices/planSlice';
import { useGetPlansQuery } from '../../store/services/authApi';
import CustomButton from '../CustomButton';

type UserInfoWrapperProps = {};

const UserInfoWrapper: FC<UserInfoWrapperProps> = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const user = useAppSelector(state => state.user);
  const auth = useAppSelector(state => state.auth);
  const [isMobileNav, setMobileNav] = useState<boolean>(false);
  const [showAvatarModal, setShowAvatarModal] = useState<boolean>(false);
  const [showLogoutModal, setShowLogoutModal] = useState<boolean>(false);

  const { data: userData } = useGetUserQuery({}, { refetchOnMountOrArgChange: true });
  const { data: plans } = useGetPlansQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });

  useEffect(() => {
    if (plans) {
      dispatch(setPlans(plans));
    }
  }, [plans, dispatch]);

  useEffect(() => {
    if (!plans || !user?.chosen_plan) return;

    const plan = plans.find(p => p.id === user.chosen_plan);

    if (plan) {
      dispatch(setSelectedPlan(plan));
    }
  }, [plans, user, dispatch]);

  useEffect(() => {
    if (userData && auth.userInfo?.access) {
      dispatch(setUserInfo(userData));
    }
  }, [userData, auth.userInfo?.access, dispatch]);

  const handleActivePage = (pageName: 'profile' | 'trips' | 'ideas' | 'documents' | 'discover') => {
    navigate(`/${pageName}`);
  };

  const handleLogout = async () => {
    await unregisterNotificationDevice();
    localStorage.removeItem('userInfo');
    clearReferralToken();
    dispatch(api.util.resetApiState());
    dispatch(logOutUser());
    dispatch(logout());
    setShowLogoutModal(false);
    setMobileNav(false);
  };

  const toggleMobileNav = () => {
    setMobileNav(!isMobileNav);
  };

  const getUserInitials = () => {
    const firstName = user.first_name?.trim();
    const lastName = user.last_name?.trim();

    if (firstName && lastName) {
      return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
    } else if (firstName) {
      return firstName.charAt(0).toUpperCase();
    } else if (user.username) {
      return user.username.charAt(0).toUpperCase();
    }
    return 'U';
  };

  const handleAvatarEditClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    setShowAvatarModal(true);
  };

  const handleAvatarModalClose = () => {
    setShowAvatarModal(false);
  };

  const handleAvatarUploadSuccess = () => {
    console.log('Avatar uploaded successfully');
  };

  const renderUsername = () => (
    <Box className={styles.usernameRow}>
      {user.referral_program_eligible && (
        <CheckCircleIcon
          size={18}
          color="#208B64"
        />
      )}
      <Typography
        variant="h10"
        className={styles.nickNameText}
      >
        @{user.username}
      </Typography>
    </Box>
  );

  const activeGradient = createGradientConfig('active-menu-gradient');

  return (
    <>
      <RealtimeNotifications />
      <Container
        maxWidth={false}
        sx={{ padding: '0 !important' }}
      >
        <Box className={styles.userInfoHeader}>
          <Box
            className={styles.logoContainer}
            onClick={() => navigate('/profile')}
          >
            <Typography variant="h6Bold">Trav</Typography>
            <img
              src={LogoIcon}
              className={styles.logo}
              alt="logo"
            />
            <Typography variant="h6Bold">Genius</Typography>
          </Box>
          <Box className={styles.navSection}>
            <NotificationsPopover />
            <Box className={styles.burger}>
              {!isMobileNav && <MenuIcon onClick={toggleMobileNav} />}
              {isMobileNav && <CloseIcon onClick={toggleMobileNav} />}
            </Box>
          </Box>
        </Box>

        <Box className={styles.userInfoContent}>
          {isMobileNav && (
            <Box className={styles.customDrawerContent}>
              <Box className={styles.infoSection}>
                <Box className={styles.mainInfo}>
                  <Box className={styles.avatarContainer}>
                    <Avatar
                      src={user.profile_picture || undefined}
                      className={styles.heroImg}
                      sx={{
                        width: 90,
                        height: 90,
                        backgroundColor: user.profile_picture ? 'transparent' : '#e3f2fd',
                        color: user.profile_picture ? 'transparent' : '#0098f0',
                        fontSize: '32px',
                        fontWeight: 600,
                      }}
                    >
                      {!user.profile_picture && getUserInitials()}
                    </Avatar>
                    <Box
                      className={styles.editIconMobile}
                      onClick={handleAvatarEditClick}
                    >
                      <EditIcon size={16} />
                    </Box>
                  </Box>
                  <Box className={styles.mainInfoText}>
                    <Typography
                      variant="h6Bold"
                      className={styles.nameText}
                    >{`${user.first_name} ${user.last_name}`}</Typography>
                    {renderUsername()}
                  </Box>
                </Box>

                <Box className={styles.mainMenu}>
                  <Box
                    className={`${styles.menuBtn} ${pathname === '/profile' ? styles.activeMenuBtn : ''}`}
                    onClick={() => {
                      toggleMobileNav();
                      handleActivePage('profile');
                    }}
                  >
                    <UserIcon gradient={pathname === '/profile' ? activeGradient : undefined} />
                    <Typography
                      variant="h10"
                      className={styles.nickNameText}
                    >
                      Profile
                    </Typography>
                  </Box>
                  <Box
                    className={`${styles.menuBtn} ${pathname === '/ideas' ? styles.activeMenuBtn : ''}`}
                    onClick={() => {
                      toggleMobileNav();
                      handleActivePage('ideas');
                    }}
                  >
                    <LikeIcon gradient={pathname === '/ideas' ? activeGradient : undefined} />
                    <Typography
                      variant="h10"
                      className={styles.nickNameText}
                    >
                      My Ideas
                    </Typography>
                  </Box>
                  <Box
                    className={`${styles.menuBtn} ${pathname === '/trips' ? styles.activeMenuBtn : ''}`}
                    onClick={() => {
                      toggleMobileNav();
                      handleActivePage('trips');
                    }}
                  >
                    <TripIcon gradient={pathname === '/trips' ? activeGradient : undefined} />
                    <Typography
                      variant="h10"
                      className={styles.nickNameText}
                    >
                      My trips
                    </Typography>
                  </Box>
                  <Box
                    className={`${styles.menuBtn} ${pathname === '/discover' ? styles.activeMenuBtn : ''}`}
                    onClick={() => {
                      toggleMobileNav();
                      handleActivePage('discover');
                    }}
                  >
                    <DiscoverIcon gradient={pathname === '/discover' ? activeGradient : undefined} />
                    <Typography
                      variant="h10"
                      className={styles.nickNameText}
                    >
                      Discover
                    </Typography>
                  </Box>
                </Box>
              </Box>

              <Box className={styles.menuExitBtnWrapper}>
                <Box
                  className={styles.menuExitBtn}
                  onClick={() => setShowLogoutModal(true)}
                >
                  <ExitIcon />
                  <Typography
                    variant="h10"
                    className={styles.nickNameText}
                  >
                    Log out
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}
          <Box className={styles.userInfoContentLeft}>
            <Box className={styles.infoSection}>
              <Box className={styles.mainInfo}>
                <Box className={styles.avatarContainer}>
                  <Avatar
                    src={user.profile_picture || undefined}
                    className={styles.heroImg}
                    sx={{
                      width: 180,
                      height: 180,
                      backgroundColor: user.profile_picture ? 'transparent' : '#e3f2fd',
                      color: user.profile_picture ? 'transparent' : '#0098f0',
                      fontSize: '48px',
                      fontWeight: 600,
                    }}
                  >
                    {!user.profile_picture && getUserInitials()}
                  </Avatar>
                  <Box
                    className={styles.editIcon}
                    onClick={handleAvatarEditClick}
                  >
                    <EditIcon
                      size={20}
                      color="white"
                    />
                  </Box>
                </Box>
                <Box className={styles.mainInfoText}>
                  <Typography
                    variant="h6Bold"
                    className={styles.nameText}
                  >{`${user.first_name} ${user.last_name}`}</Typography>
                  {renderUsername()}
                </Box>
              </Box>

              <Box className={styles.mainMenu}>
                <Box
                  className={`${styles.menuBtn} ${pathname === '/profile' ? styles.activeMenuBtn : ''}`}
                  onClick={() => handleActivePage('profile')}
                >
                  <UserIcon gradient={pathname === '/profile' ? activeGradient : undefined} />
                  <Typography
                    variant="h10"
                    className={styles.nickNameText}
                  >
                    Profile
                  </Typography>
                </Box>
                <Box
                  className={`${styles.menuBtn} ${pathname === '/ideas' ? styles.activeMenuBtn : ''}`}
                  onClick={() => handleActivePage('ideas')}
                >
                  <LikeIcon gradient={pathname === '/ideas' ? activeGradient : undefined} />

                  <Typography
                    variant="h10"
                    className={styles.nickNameText}
                  >
                    My ideas
                  </Typography>
                </Box>
                <Box
                  className={`${styles.menuBtn} ${pathname === '/trips' ? styles.activeMenuBtn : ''}`}
                  onClick={() => handleActivePage('trips')}
                >
                  <TripIcon gradient={pathname === '/trips' ? activeGradient : undefined} />
                  <Typography
                    variant="h10"
                    className={styles.nickNameText}
                  >
                    My trips
                  </Typography>
                </Box>
                <Box
                  className={`${styles.menuBtn} ${pathname === '/discover' ? styles.activeMenuBtn : ''}`}
                  onClick={() => handleActivePage('discover')}
                >
                  <DiscoverIcon gradient={pathname === '/discover' ? activeGradient : undefined} />
                  <Typography
                    variant="h10"
                    className={styles.nickNameText}
                  >
                    Discover
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Box className={styles.menuExitBtnWrapper}>
              <Box
                className={styles.menuExitBtn}
                onClick={() => setShowLogoutModal(true)}
              >
                <ExitIcon />
                <Typography
                  variant="h10"
                  className={styles.nickNameText}
                >
                  Log out
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box className={styles.userInfoContentRight}>{!isMobileNav && <Outlet />}</Box>
        </Box>
      </Container>

      <AvatarUploadModal
        open={showAvatarModal}
        onClose={handleAvatarModalClose}
        onSuccess={handleAvatarUploadSuccess}
      />

      <Modal
        open={showLogoutModal}
        onClose={() => setShowLogoutModal(false)}
        aria-labelledby="logout-modal-title"
        aria-describedby="logout-modal-description"
      >
        <Box className={styles.logoutModal}>
          <Box className={styles.logoutModalClose}>
            <CloseIcon
              sx={{ width: 30, height: 30 }}
              onClick={() => setShowLogoutModal(false)}
            />
          </Box>

          <Box className={styles.logoutModalContent}>
            <Typography
              id="logout-modal-title"
              variant="h5"
              sx={{ fontWeight: 'bold', textAlign: 'center', color: '#222B3A' }}
            >
              Are you sure?
            </Typography>
            <Typography
              id="logout-modal-description"
              variant="body1"
              sx={{ color: '#222B3A', textAlign: 'center' }}
            >
              Do you really want to log out?
            </Typography>

            <Box className={styles.logoutModalActions}>
              <CustomButton
                variant="outlined"
                onClick={() => setShowLogoutModal(false)}
              >
                Cancel
              </CustomButton>
              <CustomButton
                variant="contained"
                onClick={handleLogout}
                customClass={styles.logoutButtonModal}
              >
                Logout
              </CustomButton>
            </Box>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default UserInfoWrapper;
